import { Box, Flex, Text } from '@chakra-ui/react';
import type { CSSProperties, ReactNode } from 'react';

// Keep the native input focusable + announced by AT (unlike display:none) while
// hiding it visually behind the custom control.
const VISUALLY_HIDDEN: CSSProperties = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  margin: '-1px',
  padding: 0,
  overflow: 'hidden',
  // `clip` is deprecated in CSS; `clip-path` is the visually-hidden idiom now.
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
  border: 0,
};

export interface ToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  description?: string;
  /** Sits at the end of the row, before the switch — a TierBadge, usually. */
  trailing?: ReactNode;
  /**
   * Off, and not turnable on.
   *
   * For a setting the plan does not include. The row stays readable rather than being
   * hidden, because a setting nobody can see cannot be the reason anyone upgrades — but it
   * cannot be switched on either, so there is no way to arrange a state the import will
   * then refuse.
   */
  disabled?: boolean;
}

/** Figma-styled switch row for a boolean setting. */
export function Toggle({
  checked,
  onChange,
  label,
  description,
  trailing,
  disabled = false,
}: ToggleProps) {
  return (
    <Flex
      as="label"
      align="center"
      gap="3"
      px="2.5"
      py="2"
      borderWidth="1px"
      borderColor="figma.border"
      borderRadius="md"
      cursor={disabled ? 'not-allowed' : 'pointer'}
      opacity={disabled ? 0.6 : 1}
      _hover={disabled ? undefined : { borderColor: 'figma.borderStrong' }}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.currentTarget.checked)}
        style={VISUALLY_HIDDEN}
      />
      <Box flex="1" minW="0">
        <Text fontSize="2xs" color="figma.text" fontWeight="500">
          {label}
        </Text>
        {description && (
          <Text fontSize="9px" color="figma.textMuted" mt="0.5">
            {description}
          </Text>
        )}
      </Box>
      {trailing && (
        <Box flexShrink={0} display="flex" alignItems="center">
          {trailing}
        </Box>
      )}
      <Box
        w="32px"
        h="18px"
        borderRadius="full"
        bg={checked ? 'figma.accent' : 'figma.panelHover'}
        transition="background 0.15s"
        flexShrink={0}
        p="2px"
        display="flex"
        justifyContent={checked ? 'flex-end' : 'flex-start'}
      >
        <Box w="14px" h="14px" borderRadius="full" bg="white" />
      </Box>
    </Flex>
  );
}
